import React, { createContext, useState, useContext, useEffect, useMemo } from "react";
import { Box, CircularProgress } from '@mui/material';
import { useDirectoryContext } from './DirectoryContext';
import { IDirectorySearchContextType } from "../types/interfaces/IDirectorySearchContextType";
import { IDirectorySearchProviderProps } from "../types/interfaces/IDirectorySearchProviderProps";
import { IMember } from "../types/interfaces/IMember";
import { IFilterParams } from "../types/interfaces/IFilterParams";
import { FilterParams } from "../types/classes/FilterParams";
import { IDisplayParams } from "../types/interfaces/IDisplayParams";
import { DisplayParams } from "../types/classes/DisplayParams";

// NOTE: The following Co-Pilot suggestions are intentionally not used in this file.
// - Error Message Handling
//      Reason: The error handling is not the responsibility of this context. Errors are defined within the hooks
//      and Error messaging is done within the ErrorContext and Error component.
// - Consistent Initial State (specifically to the state setters of filterParams and displayParams):
//      Reason: The initial state of filterParams and displayParams is set to new instances of their respective classes
//      containing the needed default property values. State setters are utilized throughout the application.
// - Provide Reset Methods
//      Reason: The reset methods are not needed in this context. The filterParams and displayParams are reset
//      within the DirectorySearchProvider component when the loadingDirCtx changes to false.



export const DirectorySearchContext = createContext<IDirectorySearchContextType | undefined>(undefined);

/**
 * Custom hook to access the DirectorySearchContext.
 * Throws an error if used outside of a DirectorySearchProvider.
 * 
 * @returns {IDirectorySearchContextType} The search context value.
 */
export const useDirectorySearchContext = (): IDirectorySearchContextType => {
    const context = useContext(DirectorySearchContext);
    if (!context) {
        throw new Error('useDirectorySearchContext must be used within a DirectorySearchProvider');
    }
    return context;
};

/**
 * SearchProvider Component
 * 
 * This provider component shares SearchContext state across all child components.
 * It provides state and setters for all user actions within child components.
 * It provides state for any filters the user applies.
 * It provides state for the "filtered" member list resulting from the user's selection of filters.
 * 
 * Example usage:
 * ```tsx
 * <SearchProvider>
 *     <YourComponent />
 * </SearchProvider>
 * ```
 * 
 * @param {React.ReactNode} children - The child components that will have access to the SearchContext.
 * @returns {JSX.Element} The search context provider with the search state.
 */
export const DirectorySearchProvider: React.FC<IDirectorySearchProviderProps> = ({ children }) => {
    const { loadingDirCtx, stateOptions } = useDirectoryContext();
    const [loadingDirSearchCtx, setLoadingDirSearchCtx] = useState<boolean>(true);
    const [loadingFilteredList, setLoadingFilteredList] = useState<boolean>(false);
    const [hasAgreed, setHasAgreed] = useState<boolean>(false);
    const [isInitialState, setIsInitialState] = useState<boolean>(true);
    const [filteredMemberList, setFilteredMemberList] = useState<IMember[]>([]);
    const [filteredStateOptions, setFilteredStateOptions] = useState<string[]>([]);
    const [filterParams, setFilterParams] = useState<IFilterParams | undefined>(new FilterParams());
    const [displayParams, setDisplayParams] = useState<IDisplayParams | undefined>(new DisplayParams());

    useEffect(() => {
        if (!loadingDirCtx) {
            setFilteredMemberList([]);
            setFilteredStateOptions(stateOptions as string[]);
            setLoadingDirSearchCtx(false);
        }
    }, [loadingDirCtx]);

const value = useMemo(() => ({
    hasAgreed,
    isInitialState, 
    loadingFilteredList,
    filteredMemberList,
    filteredStateOptions,
    filterParams,
    displayParams,     
    setHasAgreed,
    setIsInitialState,
    setLoadingFilteredList,
    setFilteredMemberList,
    setFilteredStateOptions,
    setFilterParams,
    setDisplayParams
}), [
    hasAgreed,
    isInitialState,
    loadingFilteredList,
    filteredMemberList,
    filteredStateOptions,
    filterParams,
    displayParams
]);

    return (
        <DirectorySearchContext.Provider value={value}>
            {loadingDirSearchCtx ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                    <CircularProgress />
                </Box>
            ) : (
                children
            )}
        </DirectorySearchContext.Provider>
    );
};
